// Apps & rollen — de rol×app-toegangsmatrix (autorisatie Fase 2).
// Rijen = alle apps uit het nav-register (navigation.js), kolommen = de 4 rollen.
// Een cel zet een per-ROL override (view/edit) bovenop de code-default; geen override
// = standaardgedrag (13 klassieke features volgen ROLE_DEFAULTS, losse tools = open).
// Doorzoekbaar. Schrijven loopt via de admin-gated Node-proxy /Api/permissions/role-access.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Table, TableHead, TableBody, TableRow,
  TableCell, Select, MenuItem, Divider, Chip, CircularProgress, Alert, Stack, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { NAV_ITEMS } from '../../../config/navigation';
import { AI_ENGINE_URL, BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { SectionHeader } from '../adminSettingsCommon';

const ROLES = [
  { key: 'admin', label: 'Admin' },
  { key: 'management', label: 'Management' },
  { key: 'projectleider', label: 'Projectleider' },
  { key: 'team', label: 'Teamlid' },
];

// Features waar 'bewerken' (edit) óók echt server-side wordt afgedwongen (aparte
// requireFeature(...,'edit')-gates). Alleen daar bieden we het onderscheid zien vs
// bewerken aan; elders is de matrix eerlijk beperkt tot toegang ja/nee (view).
const EDIT_ENFORCED = new Set(['offertes', 'sharepoint_analyse', 'admin_settings', 'algemene_mailboxen',
  'backlog', 'feedback_beheer',
  // P1 Gap B (5 sep): sindsdien ook server-side een aparte edit-poort.
  'revenue_forecast', 'kpi_dashboard', 'strategy', 'planner_todo', 'corvee_beheer',
  // HR & leveranciers (26 sep): contract aanmaken / datacorrectie verwerken vragen edit.
  'hr_leveranciers',
  // Sleuteloverzicht (26 sep): lezen en wijzigen apart afgedwongen.
  'sleutels',
  // Budget per account (27 sep): targets en invulvelden vragen edit.
  'budget']);

// Stabiele app-sleutel: de feature-key als die bestaat, anders een slug van de route.
// Moet overeenkomen met wat de nav-enforcement (Fase 3) straks controleert.
export const appKeyForNavItem = (item) =>
  (item.feature || String(item.path || '').replace(/^\//, '').replace(/[^a-zA-Z0-9]+/g, '_')).toLowerCase();

function buildApps(features = {}) {
  const seen = new Set();
  const apps = [];
  for (const it of NAV_ITEMS) {
    if (!it.path || it.path.includes(':')) continue; // sla dynamische routes over
    const key = appKeyForNavItem(it);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    apps.push({
      key,
      label: it.label || it.path,
      category: it.category || 'Kern',
      route: it.path,
      isFeature: !!it.feature,
    });
  }
  // Rechten zonder eigen navigatie-item (bijv. Backlog en Feedbackbeheer, die als
  // sub-tab onder Beheer leven) zouden anders helemaal niet in deze matrix staan —
  // en dus alleen via de database te zetten zijn. FEATURES komt van /permissions/roles.
  for (const [key, meta] of Object.entries(features)) {
    if (seen.has(key)) continue;
    seen.add(key);
    apps.push({
      key,
      label: meta.label || key,
      category: meta.category || 'Beheer',
      route: null,
      isFeature: true,
    });
  }
  return apps.sort((a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label));
}

// Effectieve toegang van (rol, app): override wint, anders code-default, anders open (tools).
function effective(access, defaults, roleKey, app) {
  const ov = access?.[roleKey]?.[app.key];
  if (ov) return { view: !!ov.view, edit: !!ov.edit, overridden: true };
  const def = defaults?.[roleKey]?.[app.key];
  if (def) return { view: !!def.view, edit: !!def.edit, overridden: false };
  return { view: true, edit: false, overridden: false, implicitOpen: true }; // tool zonder feature-default
}

export default function AppRoleMatrix() {
  const [defaults, setDefaults] = useState({});
  const [access, setAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null); // "role:appkey" die net opslaat

  const [features, setFeatures] = useState({});
  const apps = useMemo(() => buildApps(features), [features]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [rolesRes, accessRes] = await Promise.all([
          authFetch(`${AI_ENGINE_URL}/api/v1/permissions/roles`),
          authFetch(`${BACKEND_URL}/Api/permissions/role-access`),
        ]);
        const rolesData = await rolesRes.json();
        const accessData = await accessRes.json();
        setDefaults(rolesData.defaults || {});
        setFeatures(rolesData.features || {});
        setAccess(accessData.access || {});
      } catch (e) {
        setError(e.message || 'Laden mislukt');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Bij een mislukte mutatie de echte serverstatus terughalen, zodat een cel nooit
  // ten onrechte 'geslaagd' blijft tonen (FF-REV-005).
  const reloadAccess = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/permissions/role-access`);
      const d = await r.json();
      if (r.ok) setAccess(d.access || {});
    } catch { /* laat de foutmelding staan */ }
  };

  const setCell = async (roleKey, app, next) => {
    const tag = `${roleKey}:${app.key}`;
    setBusy(tag);
    // Bewerken kan niet zonder zien.
    const view = !!next.view;
    const edit = view ? !!next.edit : false;
    // Optimistisch bijwerken.
    setAccess((a) => ({ ...a, [roleKey]: { ...(a[roleKey] || {}), [app.key]: { view, edit } } }));
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/permissions/role-access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: roleKey, app_key: app.key, view, edit }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Opslaan mislukt');
    } catch (e) {
      setError(e.message);
      reloadAccess();
    } finally {
      setBusy(null);
    }
  };

  const resetCell = async (roleKey, app) => {
    const tag = `${roleKey}:${app.key}`;
    setBusy(tag);
    setAccess((a) => {
      const r = { ...(a[roleKey] || {}) };
      delete r[app.key];
      return { ...a, [roleKey]: r };
    });
    try {
      const r = await authFetch(
        `${BACKEND_URL}/Api/permissions/role-access?role=${encodeURIComponent(roleKey)}&app_key=${encodeURIComponent(app.key)}`,
        { method: 'DELETE' },
      );
      if (!r.ok) throw new Error('Reset mislukt');
    } catch (e) {
      setError(e.message);
      reloadAccess();
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return apps;
    return apps.filter((a) =>
      a.label.toLowerCase().includes(needle) ||
      a.category.toLowerCase().includes(needle) ||
      a.key.includes(needle));
  }, [apps, q]);

  return (
    <Box>
      <SectionHeader
        title="Apps & rollen"
        subtitle="Kies per rol per app het toegangsniveau: geen toegang, alleen zien, of zien + bewerken. Een aangepaste cel is geel (override op de standaard) — kies '↺ Terug naar standaard' om 'm te resetten. Losse tools zonder override zijn voor iedereen zichtbaar."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TextField
        size="small"
        fullWidth
        placeholder="Zoek app op naam, categorie of sleutel…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ mb: 2, maxWidth: 460 }}
        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>App ({filtered.length})</TableCell>
                {ROLES.map((r) => (
                  <TableCell key={r.key} align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{r.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((app) => (
                <TableRow key={app.key} hover>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>{app.label}</Typography>
                      {!app.isFeature && (
                        <Tooltip title="Deze tool heeft nog geen recht-koppeling — hij wordt server-side niet afgedwongen. Instellen kan pas na de enforcement-fase.">
                          <Chip size="small" label="nog niet afgedwongen" sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {app.category}{app.isFeature ? '' : ' · tool'}{app.route ? ` · ${app.route}` : ''}
                    </Typography>
                  </TableCell>
                  {ROLES.map((role) => {
                    const e = effective(access, defaults, role.key, app);
                    const tag = `${role.key}:${app.key}`;
                    // Ongegate tool: server-side (nog) niet afdwingbaar → read-only, niet doen alsof.
                    if (!app.isFeature) {
                      return (
                        <TableCell key={role.key} align="center">
                          <Tooltip title="Deze tool wordt server-side nog niet afgedwongen — instellen kan pas na de enforcement-fase.">
                            <Chip size="small" label="open" variant="outlined" sx={{ height: 20, fontSize: 11, color: '#94a3b8' }} />
                          </Tooltip>
                        </TableCell>
                      );
                    }
                    const editEnforced = EDIT_ENFORCED.has(app.key);
                    // Zonder edit-enforcement tonen we geen 'bewerken'-optie (zou een loze belofte zijn).
                    const stateKey = !e.view ? 'none' : (e.edit && editEnforced ? 'edit' : 'view');
                    const onSel = (val) => {
                      if (val === 'reset') return resetCell(role.key, app);
                      if (val === 'none') return setCell(role.key, app, { view: false, edit: false });
                      if (val === 'view') return setCell(role.key, app, { view: true, edit: false });
                      return setCell(role.key, app, { view: true, edit: true });
                    };
                    return (
                      <TableCell key={role.key} align="center" sx={{ bgcolor: e.overridden ? '#fffbeb' : undefined }}>
                        <Select
                          size="small" value={stateKey} disabled={busy === tag}
                          onChange={(ev) => onSel(ev.target.value)}
                          sx={{ minWidth: 158, fontSize: 13, bgcolor: '#fff' }}
                        >
                          <MenuItem value="none" sx={{ fontSize: 13 }}>🚫 Geen toegang</MenuItem>
                          <MenuItem value="view" sx={{ fontSize: 13 }}>{editEnforced ? '👁 Alleen zien' : '✅ Toegang'}</MenuItem>
                          {editEnforced && <MenuItem value="edit" sx={{ fontSize: 13 }}>✏️ Zien + bewerken</MenuItem>}
                          {e.overridden && <Divider />}
                          {e.overridden && <MenuItem value="reset" sx={{ fontSize: 13, color: 'text.secondary' }}>↺ Terug naar standaard</MenuItem>}
                        </Select>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" label="🚫 geen · 👁 zien · ✏️ bewerken (alleen waar afgedwongen)" variant="outlined" />
        <Chip size="small" label="'open' = tool nog niet afgedwongen (read-only)" variant="outlined" />
        <Chip size="small" label="Gele cel = afwijkend van standaard" sx={{ bgcolor: '#fffbeb' }} />
      </Stack>
    </Box>
  );
}
